import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { tripAPI, promoAPI } from '../../services/api';
import api from '../../services/api';
import { useLanguage } from '../../hooks/useLanguage';
import AppNavbar from '../../components/AppNavbar';
import LegalAcceptanceCheckbox from '../../components/legal/LegalAcceptanceCheckbox';
import { generateVehicleLayout } from '../../utils/seatLayout';

const toArNum = (n) => String(n).replace(/[0-9]/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);
const arPrice = (n) => toArNum(parseFloat(n || 0).toLocaleString('en-US'));

/* ─── Seat button ────────────────────────────────────────────────────────────
   free     → white bg, gray border #D1D5DB
   selected → blue  bg #1D4ED8, white number
   booked   → white bg, orange border #F97316, orange lock badge, gray number
─────────────────────────────────────────────────────────────────────────── */
const SeatBtn = ({ n, isBooked, isSelected, onToggle, small = false }) => {
  const state = isBooked ? 'booked' : isSelected ? 'selected' : 'free';
  const w = small ? 44 : 52;
  const h = small ? 50 : 56;
  return (
    <button
      type="button"
      onClick={() => !isBooked && onToggle(n)}
      disabled={isBooked}
      title={`مقعد ${toArNum(n)}`}
      aria-pressed={isSelected}
      className={`relative shrink-0 flex items-center justify-center select-none transition-all duration-150 ${
        isBooked   ? 'cursor-not-allowed' :
        isSelected ? 'cursor-pointer active:scale-95' :
                     'cursor-pointer hover:scale-105 active:scale-95'
      }`}
      style={{
        width: w, height: h,
        background: state === 'selected' ? '#dcfce7' : '#ffffff',
        borderRadius: 10,
        border: state === 'selected' ? '3px solid #15803d'
              : state === 'booked'   ? '2px solid #f97316'
              :                        '2px solid #d1d5db',
        boxShadow: state === 'selected'
          ? '0 0 0 4px rgba(21,128,61,0.40)'
          : state === 'booked'
          ? '0 0 0 2px rgba(249,115,22,0.18)'
          : '0 2px 6px rgba(0,0,0,0.10)',
        padding: 0,
      }}
    >
      {/* Seat number */}
      <span style={{
        fontSize: 16,
        fontWeight: 800,
        lineHeight: 1,
        color: state === 'selected' ? '#15803d'
             : state === 'booked'   ? '#9ca3af'
             :                        '#374151',
        fontFamily: 'Cairo, sans-serif',
        userSelect: 'none',
      }}>
        {toArNum(n)}
      </span>

      {/* Orange lock badge — booked seats only */}
      {isBooked && (
        <span style={{ position: 'absolute', top: 3, right: 3 }}>
          <svg width="13" height="14" viewBox="0 0 13 14" fill="none">
            <rect x="1.5" y="6" width="10" height="7.5" rx="2" fill="#f97316"/>
            <path d="M 3.5 6 V 4.5 a 3 3 0 0 1 6 0 V 6"
              stroke="#f97316" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
            <circle cx="6.5" cy="9.8" r="1.3" fill="#ffffff"/>
          </svg>
        </span>
      )}
    </button>
  );
};

const API_HOST = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
const resolveLayoutImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_HOST}${url}`;
};

/* ══════════════════════════════════════════════════════════════════════════
   SeatDiagram — VERTICAL portrait layout
   Front of bus at TOP, back at BOTTOM.
   2+2 layout: left-pair | white aisle strip | right-pair
   Emergency exit gap at the midpoint of the seat section.
   5-seat back bench spanning full width.
══════════════════════════════════════════════════════════════════════════ */
const SeatDiagram = ({ bookedSeats = [], selectedSeats, onToggle, t, bus, totalSeats: propTotalSeats }) => {
  // bus.totalSeats (from the seat template) is the authoritative seat count.
  // propTotalSeats (bookedSeats + availableSeats) excludes blocked seats so we avoid it as the primary source.
  const totalSeats = bus?.totalSeats || propTotalSeats || 45;
  const { cfg, seatsPerRow, rows, backSeats } = generateVehicleLayout(totalSeats, bus);

  /* ── Exit gap after the row at ~mid-point of the seat section ── */
  const exitAfterRow = Math.floor(rows.length * 0.5);

  /* ── Convenience wrapper ── */
  const S = (n) => (
    <SeatBtn key={n} n={n}
      isBooked={bookedSeats.includes(n)}
      isSelected={selectedSeats.includes(n)}
      onToggle={onToggle}
    />
  );

  /* ── Blocked slot — deliberately empty seat position (e.g. van sliding door) ── */
  const BlockedSlot = () => (
    <div style={{
      width: 52, height: 56, borderRadius: 10,
      background: 'rgba(255,255,255,0.04)',
      border: '2px dashed rgba(255,255,255,0.12)',
      flexShrink: 0,
    }} />
  );

  /* ── Seat legend ── */
  const Legend = () => (
    <div dir="rtl" className="flex items-center justify-center gap-5 mb-3">
      {/* Available */}
      <div className="flex items-center gap-1.5">
        <div style={{
          width: 18, height: 18, borderRadius: 4,
          background: '#ffffff', border: '2px solid #d1d5db',
          boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
        }} />
        <span className="text-xs text-gray-500">متاح</span>
      </div>
      {/* Selected */}
      <div className="flex items-center gap-1.5">
        <div style={{
          width: 18, height: 18, borderRadius: 4,
          background: '#dcfce7', border: '3px solid #15803d',
          boxShadow: '0 0 0 3px rgba(21,128,61,0.35)',
        }} />
        <span className="text-xs text-gray-500">محدد</span>
      </div>
      {/* Booked */}
      <div className="flex items-center gap-1.5">
        <div style={{
          width: 18, height: 18, borderRadius: 4,
          background: '#ffffff', border: '2px solid #f97316',
          boxShadow: '0 0 0 2px rgba(249,115,22,0.15)',
          position: 'relative', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="9" height="10" viewBox="0 0 9 10" fill="none">
            <rect x="1" y="3.8" width="7" height="5.8" rx="1.5" fill="#f97316"/>
            <path d="M 2.2 3.8 V 2.8 a 2.3 2.3 0 0 1 4.6 0 v 1"
              stroke="#f97316" strokeWidth="1.4" fill="none" strokeLinecap="round"/>
          </svg>
        </div>
        <span className="text-xs text-gray-500">محجوز</span>
      </div>
    </div>
  );

  return (
    <div dir="ltr" className="select-none">

      {/* ── Legend ── */}
      <Legend />

      {/* ── Direction label top ── */}
      <div className="flex items-center justify-end gap-1 mb-2 pr-1" dir="rtl">
        <span className="text-[11px] text-gray-600 font-semibold">أمام الباص</span>
        <span className="text-gray-500 text-xs">→</span>
      </div>

      {/* ── Bus body ── */}
      <div
        className="relative mx-auto overflow-hidden"
        style={{
          width: 270,
          background: '#12192b',
          borderRadius: 36,
          padding: '14px 10px',
          /* Metallic bus shell effect */
          boxShadow: [
            'inset 0 0 0 4px #2d3748',    /* inner wall */
            'inset 0 0 0 7px #1a202c',    /* body */
            '0 0 0 3px #4a5568',           /* chrome trim */
            '0 0 0 5px #718096',           /* outer chrome */
            '0 8px 40px rgba(0,0,0,0.6)', /* drop shadow */
          ].join(', '),
        }}
      >

        {/* Blue LED strips on sides */}
        <div style={{
          position: 'absolute', left: 5, top: 90, bottom: 90,
          width: 5, borderRadius: 3,
          background: 'linear-gradient(180deg,#3b82f6 0%,#1d4ed8 50%,#3b82f6 100%)',
          opacity: 0.85,
        }} />
        <div style={{
          position: 'absolute', right: 5, top: 90, bottom: 90,
          width: 5, borderRadius: 3,
          background: 'linear-gradient(180deg,#3b82f6 0%,#1d4ed8 50%,#3b82f6 100%)',
          opacity: 0.85,
        }} />

        {/* ── Dashboard / Front area + Driver seats (compact SVG) ── */}
        <svg width="250" height="176" viewBox="0 0 250 176" fill="none"
          style={{ display: 'block', marginBottom: 8 }}>

          {/* Background */}
          <rect width="250" height="176" rx="13" fill="#080c15"/>

          {/* Top dashboard band */}
          <rect x="0" y="0" width="250" height="28" fill="#0b1020"/>
          <rect x="0" y="17" width="250" height="11" fill="#080c15"/>

          {/* Left grille bars */}
          {[0,1,2,3,4,5].map(i =>
            <rect key={'lg'+i} x="10" y={5+i*4} width="82" height="2" rx="1" fill="#162030"/>
          )}

          {/* Center camera unit */}
          <rect x="106" y="4" width="38" height="20" rx="5" fill="#060912"/>
          <rect x="110" y="7" width="30" height="14" rx="4" fill="#03060c"/>
          <circle cx="125" cy="14" r="6" fill="#020509"/>
          <circle cx="125" cy="14" r="3.5" fill="#0c1525"/>
          <circle cx="123.5" cy="12.5" r="0.8" fill="#1c2b40"/>

          {/* Right grille bars */}
          {[0,1,2,3,4,5].map(i =>
            <rect key={'rg'+i} x="158" y={5+i*4} width="82" height="2" rx="1" fill="#162030"/>
          )}

          {/* ── Steering wheel — large, LEFT (driver side) ── */}
          <circle cx="75" cy="77" r="43" fill="#000" opacity="0.3"/>
          <circle cx="75" cy="75" r="43" fill="#0d1522" stroke="#192434" strokeWidth="9"/>
          <circle cx="75" cy="75" r="33" fill="none" stroke="#101928" strokeWidth="1.5"/>
          {/* Spokes */}
          <line x1="75" y1="32"  x2="75"  y2="62"  stroke="#0a1420" strokeWidth="9" strokeLinecap="round"/>
          <line x1="38"  y1="98" x2="61"  y2="86"  stroke="#0a1420" strokeWidth="9" strokeLinecap="round"/>
          <line x1="112" y1="98" x2="89"  y2="86"  stroke="#0a1420" strokeWidth="9" strokeLinecap="round"/>
          {/* Hub */}
          <circle cx="75" cy="75" r="16" fill="#0e1a2a"/>
          <circle cx="75" cy="75" r="10" fill="#111d2e"/>
          <circle cx="75" cy="75" r="5.5" fill="#080e1c"/>
          {/* Rim shine */}
          <path d="M 44 49 A 42 42 0 0 1 106 49" stroke="#213248" strokeWidth="2" fill="none" strokeLinecap="round"/>

          {/* ── Right instrument panel ── */}
          <rect x="132" y="28" width="112" height="86" rx="8" fill="#080e1c"/>
          {[0,1,2,3,4,5,6,7].map(i =>
            <rect key={'vl'+i} x="140" y={34+i*6} width="96" height="2.5" rx="1.25" fill="#101a2c"/>
          )}
          {/* Display unit */}
          <rect x="140" y="86" width="96" height="22" rx="6" fill="#060a12"/>
          <rect x="144" y="89" width="88" height="16" rx="4" fill="#040810"/>
          <rect x="148" y="93"  width="32" height="3" rx="1.5" fill="#0e1826"/>
          <rect x="148" y="98"  width="52" height="2.5" rx="1.25" fill="#0c1523"/>
          <rect x="204" y="93"  width="16" height="3" rx="1.5" fill="#0e1826" opacity="0.5"/>

          {/* ── Seat area separator ── */}
          <line x1="4" y1="118" x2="246" y2="118" stroke="#0d1726" strokeWidth="1"/>

          {/* LEFT SEAT — السائق */}
          <rect x="6"   y="120" width="44" height="20" rx="6" fill="#1a2636"/>
          <rect x="52"  y="120" width="14" height="16" rx="3" fill="#05080e"/>
          <rect x="68"  y="120" width="44" height="20" rx="6" fill="#1a2636"/>
          <rect x="4"   y="142" width="112" height="22" rx="8" fill="#162232"/>
          <rect x="9"   y="146" width="102" height="14" rx="6" fill="#1d2c3f"/>
          <rect x="9"   y="155" width="102" height="3"  rx="1.5" fill="#0f1b2b"/>
          <rect x="6"   y="165" width="108" height="7"  rx="3" fill="#111d2c"/>
          <text x="60" y="156" textAnchor="middle" fontSize="10" fill="#7a8fa6"
            fontFamily="Cairo, Arial, sans-serif" fontWeight="700">السائق</text>

          {/* Center console */}
          <rect x="118" y="118" width="14" height="55" rx="3" fill="#040710"/>
          <rect x="121" y="124" width="8"  height="7"  rx="2" fill="#0a1320"/>
          <rect x="121" y="135" width="8"  height="4"  rx="1.5" fill="#0a1320"/>
          <rect x="121" y="143" width="8"  height="4"  rx="1.5" fill="#0a1320"/>
          <rect x="122" y="151" width="6"  height="14" rx="2" fill="#08111e"/>

          {/* RIGHT SEAT — مساعد السائق */}
          <rect x="134" y="120" width="44" height="20" rx="6" fill="#1a2636"/>
          <rect x="180" y="120" width="14" height="16" rx="3" fill="#05080e"/>
          <rect x="196" y="120" width="44" height="20" rx="6" fill="#1a2636"/>
          <rect x="132" y="142" width="112" height="22" rx="8" fill="#162232"/>
          <rect x="137" y="146" width="102" height="14" rx="6" fill="#1d2c3f"/>
          <rect x="137" y="155" width="102" height="3"  rx="1.5" fill="#0f1b2b"/>
          <rect x="134" y="165" width="108" height="7"  rx="3" fill="#111d2c"/>
          <text x="188" y="156" textAnchor="middle" fontSize="10" fill="#7a8fa6"
            fontFamily="Cairo, Arial, sans-serif" fontWeight="700">مساعد السائق</text>

          {/* Side LED accents — seat area */}
          <rect x="0"   y="120" width="4" height="56" rx="2" fill="#1e4a8a" opacity="0.8"/>
          <rect x="246" y="120" width="4" height="56" rx="2" fill="#1e4a8a" opacity="0.8"/>
        </svg>

        {/* ── Passenger seat rows ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map(({ left, right, leftBlocked, rightBlocked }, ri) => (
            <React.Fragment key={ri}>

              {/* Emergency exit gap at midpoint */}
              {ri === exitAfterRow && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 0', margin: '2px 0',
                }}>
                  <div style={{ flex: 1, height: 1, background: 'rgba(251,191,36,0.25)' }} />
                  <span style={{ fontSize: 9, color: 'rgba(251,191,36,0.6)', whiteSpace: 'nowrap', fontFamily: 'Cairo, sans-serif' }}>
                    مخرج طوارئ
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(251,191,36,0.25)' }} />
                </div>
              )}

              {/* One row: left-pair | aisle | right-pair */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
                {/* Left pair */}
                <div style={{ display: 'flex', gap: 5 }}>
                  {left.map(n => S(n))}
                  {leftBlocked && <BlockedSlot />}
                </div>
                {/* White aisle strip */}
                <div style={{
                  width: 28, alignSelf: 'stretch', minHeight: 56,
                  background: '#ffffff',
                  margin: '0 5px',
                  borderRadius: 3,
                  opacity: 0.08,
                }} />
                {/* Right pair */}
                <div style={{ display: 'flex', gap: 5 }}>
                  {right.map(n => S(n))}
                  {rightBlocked && <BlockedSlot />}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* ── Back bench ── 5 seats × 44 px + 4 gaps × 3 px = 232 px → fits the 250 px inner width */}
        {backSeats.length > 0 && (
          <div style={{
            marginTop: 10, paddingTop: 10,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', justifyContent: 'center', gap: 3,
          }}>
            {backSeats.map(n => (
              <SeatBtn key={n} n={n}
                isBooked={bookedSeats.includes(n)}
                isSelected={selectedSeats.includes(n)}
                onToggle={onToggle}
                small
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Direction label bottom ── */}
      <div className="flex items-center justify-end gap-1 mt-2 pr-1" dir="rtl">
        <span className="text-[11px] text-gray-600 font-semibold">مؤخرة الباص</span>
        <span className="text-gray-500 text-xs">→</span>
      </div>
    </div>
  );
};

// ── Universal booking progress bar ───────────────────────────────────────────
const BookingProgressBar = ({ step }) => {
  const steps = [
    { label: 'بحث'     },
    { label: 'رحلة'    },
    { label: 'مقاعد'   },
    { label: 'مسافرون' },
    { label: 'دفع'     },
    { label: 'تم'      },
  ];
  const active = (() => {
    if (['seats', 'return_seats'].includes(step)) return 2;
    if (step === 'passengers') return 3;
    if (['bank', 'receipt', 'receipt_return'].includes(step)) return 4;
    if (step === 'done') return 5;
    return 2;
  })();
  return (
    <div className="flex items-center" dir="ltr">
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <div className="flex flex-col items-center gap-1.5 shrink-0">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-black border-2 transition-all shadow-sm ${
              i < active    ? 'bg-[#1E40AF] border-[#1E40AF] text-white shadow-[0_2px_8px_rgba(30,64,175,.3)]'
            : i === active  ? 'bg-[#EEF2FF] border-[#1E40AF] text-[#1E40AF]'
                            : 'bg-white border-[#E5E7EB] text-gray-300'
            }`}>
              {i < active ? (
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                </svg>
              ) : <span>{toArNum(i + 1)}</span>}
            </div>
            <span className={`text-[10px] font-bold text-center whitespace-nowrap ${
              i <= active ? 'text-[#1E40AF]' : 'text-gray-300'
            }`}>{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-[2px] mb-5 mx-1 transition-colors rounded-full ${
              i < active ? 'bg-[#1E40AF]' : 'bg-[#E5E7EB]'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── Persistent booking context bar (sticky, shown throughout booking flow) ────
const BookingContextBar = ({ trip, returnTrip, step, selectedSeats, returnSelectedSeats, grandTotal, bookingNumber }) => {
  const fmt   = (dt) => dt ? new Date(dt).toLocaleDateString('ar-SA', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '';
  const fmtT  = (dt) => dt ? new Date(dt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) : '';
  const allSeats = [...(selectedSeats||[]), ...(returnSelectedSeats||[])];
  const activeStep = (() => {
    if (['seats', 'return_seats'].includes(step)) return 3;
    if (step === 'passengers') return 4;
    if (['bank', 'receipt', 'receipt_return'].includes(step)) return 5;
    if (step === 'done') return 6;
    return 3;
  })();
  return (
    <div className="sticky top-[68px] z-30 bg-white border-b border-[#E5E7EB] shadow-[0_2px_8px_rgba(30,58,138,.06)]">
      <div className="max-w-2xl mx-auto px-4 py-2.5 flex items-center gap-0 overflow-x-auto scrollbar-hide" dir="rtl">
        {/* Route */}
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
          <span className="text-sm font-black text-[#1E3A8A]">{trip?.fromCity}</span>
          <svg className="w-3.5 h-3.5 text-gray-400 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
          </svg>
          <span className="text-sm font-black text-[#1E3A8A]">{trip?.toCity}</span>
        </div>
        <span className="text-gray-200 mx-2 flex-shrink-0">|</span>
        {/* Date + time */}
        <span className="text-[13px] text-gray-700 font-semibold flex-shrink-0">{fmt(trip?.departureTime)} · {toArNum(fmtT(trip?.departureTime))}</span>
        {/* Seats */}
        {allSeats.length > 0 && (
          <>
            <span className="text-gray-200 mx-2 flex-shrink-0">|</span>
            <span className="text-[13px] text-[#1E3A8A] font-bold flex-shrink-0">
              مقعد {allSeats.slice().sort((a,b)=>a-b).map(toArNum).join('، ')}
            </span>
          </>
        )}
        {/* Price */}
        {grandTotal > 0 && (
          <>
            <span className="text-gray-200 mx-2 flex-shrink-0">|</span>
            <span className="text-[13px] text-emerald-600 font-black flex-shrink-0 font-mono tabular-nums">
              {arPrice(grandTotal)} ج.س
            </span>
          </>
        )}
        {/* Booking number (after created) */}
        {bookingNumber && (
          <>
            <span className="text-gray-200 mx-2 flex-shrink-0">|</span>
            <span className="text-[12px] font-mono font-bold text-gray-500 flex-shrink-0">#{bookingNumber}</span>
          </>
        )}
        {/* Step pill - pushed to start (RTL = left) */}
        <div className="mr-auto flex-shrink-0 bg-[#EEF2FF] border border-[#C7D2FE] px-2.5 py-1 rounded-lg ml-2">
          <span className="text-[#1E40AF] text-[11px] font-black whitespace-nowrap">الخطوة {toArNum(activeStep)} من ٦</span>
        </div>
      </div>
    </div>
  );
};

// ── Round-trip step progress bar ──────────────────────────────────────────────
const RoundTripProgress = ({ step, isRTL, hasTwoReceipts }) => {
  const steps = hasTwoReceipts ? [
    { key: 'seats',          icon: '🛫', labelAr: 'مقاعد الذهاب',    labelEn: 'Outbound Seats'   },
    { key: 'return_seats',   icon: '🛬', labelAr: 'مقاعد الإياب',    labelEn: 'Return Seats'     },
    { key: 'passengers',     icon: '👤', labelAr: 'بيانات المسافرين', labelEn: 'Passengers'       },
    { key: 'bank',           icon: '💳', labelAr: 'طريقة الدفع',     labelEn: 'Payment'          },
    { key: 'receipt',        icon: '📤', labelAr: 'إيصال الذهاب',    labelEn: 'Outbound Receipt' },
    { key: 'receipt_return', icon: '📤', labelAr: 'إيصال الإياب',    labelEn: 'Return Receipt'   },
    { key: 'done',           icon: '✅', labelAr: 'تم',              labelEn: 'Done'             },
  ] : [
    { key: 'seats',        icon: '🛫', labelAr: 'مقاعد الذهاب',    labelEn: 'Outbound Seats' },
    { key: 'return_seats', icon: '🛬', labelAr: 'مقاعد الإياب',    labelEn: 'Return Seats'   },
    { key: 'passengers',   icon: '👤', labelAr: 'بيانات المسافرين', labelEn: 'Passengers'     },
    { key: 'bank',         icon: '💳', labelAr: 'طريقة الدفع',     labelEn: 'Payment'        },
    { key: 'receipt',      icon: '📤', labelAr: 'الإيصال',         labelEn: 'Receipt'        },
    { key: 'done',         icon: '✅', labelAr: 'تم',              labelEn: 'Done'           },
  ];
  const currentIndex = steps.findIndex(s => s.key === step);
  return (
    <div className="flex items-start py-1">
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <div className="flex flex-col items-center gap-1 shrink-0">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 transition-all
              ${i < currentIndex  ? 'bg-blue-700 border-blue-700 text-white'
              : i === currentIndex ? 'bg-blue-50 border-blue-500 text-blue-700'
              : 'bg-white border-gray-200 text-gray-600'}`}>
              {i < currentIndex ? '✓' : s.icon}
            </div>
            <span className={`text-[9px] font-medium text-center leading-tight max-w-[48px]
              ${i <= currentIndex ? 'text-blue-700' : 'text-gray-600'}`}>
              {isRTL ? s.labelAr : s.labelEn}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-0.5 mt-3.5 mx-0.5 ${i < currentIndex ? 'bg-blue-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── Bank accounts transfer card ───────────────────────────────────────────────
const BankAccountsCard = ({ company, amount, accentColor = 'blue', isRTL }) => {
  const accounts = company?.bankAccounts || [];
  if (accounts.length === 0) return null;

  const border  = accentColor === 'purple' ? 'border-purple-200 bg-purple-50'  : 'border-emerald-200 bg-emerald-50';
  const heading = accentColor === 'purple' ? 'text-purple-800' : 'text-emerald-800';
  const sub     = accentColor === 'purple' ? 'text-purple-600' : 'text-emerald-700';
  const badge   = accentColor === 'purple' ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700';

  return (
    <div className={`rounded-xl border p-4 mb-4 ${border}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">🏦</span>
        <div>
          <p className={`text-sm font-bold ${heading}`}>
            {isRTL ? `تفاصيل التحويل البنكي — ${company.name}` : `Bank Transfer Details — ${company.name}`}
          </p>
          <p className={`text-xs ${sub} mt-0.5`}>
            {isRTL
              ? 'حوّل المبلغ لأحد الحسابات التالية ثم ارفع الإيصال'
              : 'Transfer the amount to one of the accounts below, then upload the receipt'}
          </p>
        </div>
      </div>

      <div className="space-y-2 mb-3">
        {accounts.map((acc, i) => (
          <div key={i} className="bg-white rounded-lg px-4 py-3 border border-white/60 shadow-sm">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${badge}`}>{acc.bankName}</span>
              <span className="text-xs text-gray-600">{isRTL ? 'انقر للنسخ' : 'tap to copy'}</span>
            </div>
            <div className="mt-2 space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">{isRTL ? 'رقم الحساب:' : 'Account No:'}</span>
                <button
                  onClick={() => { navigator.clipboard?.writeText(acc.accountNumber); }}
                  className="font-mono text-sm font-bold text-gray-800 hover:text-blue-700 transition"
                  dir="ltr"
                >
                  {acc.accountNumber}
                </button>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">{isRTL ? 'اسم صاحب الحساب:' : 'Account holder:'}</span>
                <span className="text-sm font-medium text-gray-700">{acc.accountHolder}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-white/60">
        <span className="text-sm font-semibold text-gray-700">{isRTL ? 'المبلغ المطلوب:' : 'Amount to transfer:'}</span>
        <span className="text-xl font-bold text-emerald-600">{arPrice(amount)} ج.س</span>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const PassengerBookPage = () => {
  const { tripId }       = useParams();
  const navigate         = useNavigate();
  const [searchParams]   = useSearchParams();
  const returnTripId     = searchParams.get('returnTripId');
  const requestedSeats   = Math.max(1, parseInt(searchParams.get('seats') || '1', 10));
  // Smart Group Transportation System (Phase 1) — absent/'individual' behaves
  // exactly as before (submits to the existing /bookings route, unchanged).
  const bookingType      = searchParams.get('bookingType') || 'individual';
  const groupName        = searchParams.get('groupName') || '';
  const [autoSuggesting, setAutoSuggesting] = useState(false);
  const { t, lang, toggleLang, isRTL } = useLanguage();

  const [trip, setTrip]               = useState(null);
  const [returnTrip, setReturnTrip]   = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);

  // Seat selections
  const [selectedSeats, setSelectedSeats]             = useState([]);
  const [returnSelectedSeats, setReturnSelectedSeats] = useState([]);

  // Seats other viewers of the same trip have merely tapped (soft hold via
  // seatHoldService.js) — not yet booked, but shown exactly like a booked
  // seat so two passengers can't race to pick the same one.
  const [heldSeats, setHeldSeats]             = useState([]);
  const [returnHeldSeats, setReturnHeldSeats] = useState([]);
  const socketRef = useRef(null);

  // Per-seat passenger details
  const [passengerDetails, setPassengerDetails]             = useState([]);
  const [returnPassengerDetails, setReturnPassengerDetails] = useState([]);

  const [receipt, setReceipt] = useState(null);

  const storedUser = JSON.parse(localStorage.getItem('passenger_user') || '{}');

  // Steps: 'seats' | 'return_seats' | 'passengers' | 'bank' | 'receipt' | 'receipt_return' | 'done'
  const [step, setStep] = useState('seats');

  // Booking results
  const [bookingId, setBookingId]                     = useState(null);
  const [bookingNumber, setBookingNumber]             = useState(null);
  const [returnBookingId, setReturnBookingId]         = useState(null);
  const [returnBookingNumber, setReturnBookingNumber] = useState(null);

  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [legalAccepted, setLegalAccepted] = useState(false);

  // Promo code
  const [promoCode, setPromoCode]         = useState('');
  const [promoApplied, setPromoApplied]   = useState(null); // { code, title, discountPercent }
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoError, setPromoError]       = useState('');

  const isPassengerLoggedIn = !!JSON.parse(localStorage.getItem('passenger_user') || 'null')?.id;

  // The seat-conflict socket listener below lives for the whole page lifetime
  // (not just the seats step), so it needs a way to read the *current* step
  // without resubscribing the socket on every step change — a ref gives it
  // that without the staleness of a closed-over state value.
  const stepRef = useRef(step);
  useEffect(() => { stepRef.current = step; }, [step]);

  // A conflict/validation error belongs to the step it was raised on — clear
  // it on every transition instead of relying on each transition function to
  // remember to, which is how a stale error survived onto the confirmation
  // screen in the first place.
  useEffect(() => { setError(''); }, [step]);

  // Auto-apply promo code saved from the offers page
  useEffect(() => {
    if (!isPassengerLoggedIn) return;
    const pending = localStorage.getItem('safer_pending_promo');
    if (!pending) return;
    localStorage.removeItem('safer_pending_promo');
    promoAPI.validate(pending)
      .then(res => setPromoApplied(res.data.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [tripRes, retRes] = await Promise.all([
          tripAPI.getById(tripId),
          returnTripId ? tripAPI.getById(returnTripId) : Promise.resolve(null),
        ]);
        setTrip(tripRes.data.data);
        if (retRes) setReturnTrip(retRes.data.data);
      } catch {
        navigate('/');
      }
    };
    fetchAll();
  }, [tripId, returnTripId]); // eslint-disable-line

  // Live seat-conflict guard — same backend broadcast TripTrackingPage.jsx
  // already consumes for GPS (trip:${id} room via seatUpdateService.js), now
  // reused here so a seat taken by another passenger mid-selection disappears
  // immediately instead of only surfacing as a 400 at final submit.
  useEffect(() => {
    const serverUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
    const socket = io(serverUrl, { transports: ['websocket', 'polling'], withCredentials: true });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('passenger:trackTrip', tripId);
      if (returnTripId) socket.emit('passenger:trackTrip', returnTripId);
    });

    socket.on('trip:seatsUpdated', (data) => {
      // Once the user has moved past the seat-selection step, their own
      // booking submission is what puts their selected seats into
      // bookedSeats — that's success, not a conflict. Only flag a lost
      // seat as "someone else took it" while still actively selecting.
      const stillSelecting = stepRef.current === 'seats' || stepRef.current === 'return_seats';
      if (data.tripId === tripId) {
        setTrip((prev) => prev && ({ ...prev, bookedSeats: data.bookedSeats, availableSeats: data.availableSeats }));
        setSelectedSeats((prev) => {
          const lost = prev.filter((s) => data.bookedSeats?.includes(s));
          if (lost.length && stillSelecting) setError('تم حجز أحد المقاعد التي اخترتها من راكب آخر — فضلاً اختر مقعداً آخر');
          return stillSelecting ? prev.filter((s) => !data.bookedSeats?.includes(s)) : prev;
        });
      } else if (returnTripId && data.tripId === returnTripId) {
        setReturnTrip((prev) => prev && ({ ...prev, bookedSeats: data.bookedSeats, availableSeats: data.availableSeats }));
        setReturnSelectedSeats((prev) => {
          const lost = prev.filter((s) => data.bookedSeats?.includes(s));
          if (lost.length && stillSelecting) setError('تم حجز أحد المقاعد التي اخترتها من راكب آخر — فضلاً اختر مقعداً آخر');
          return stillSelecting ? prev.filter((s) => !data.bookedSeats?.includes(s)) : prev;
        });
      }
    });

    // Someone else's tap, not a real booking yet — gray the seat out on our
    // grid too so we never both try to select it.
    socket.on('trip:seatHoldsSnapshot', (data) => {
      if (data.tripId === tripId) setHeldSeats((prev) => [...new Set([...prev, ...(data.heldSeats || [])])]);
      else if (returnTripId && data.tripId === returnTripId) setReturnHeldSeats((prev) => [...new Set([...prev, ...(data.heldSeats || [])])]);
    });
    socket.on('trip:seatHeld', (data) => {
      if (data.tripId === tripId) setHeldSeats((prev) => prev.includes(data.seatNumber) ? prev : [...prev, data.seatNumber]);
      else if (returnTripId && data.tripId === returnTripId) setReturnHeldSeats((prev) => prev.includes(data.seatNumber) ? prev : [...prev, data.seatNumber]);
    });
    socket.on('trip:seatReleased', (data) => {
      if (data.tripId === tripId) setHeldSeats((prev) => prev.filter((s) => s !== data.seatNumber));
      else if (returnTripId && data.tripId === returnTripId) setReturnHeldSeats((prev) => prev.filter((s) => s !== data.seatNumber));
    });
    // Our own tap lost a race to someone else's between the click and the
    // server processing it — undo the optimistic local selection.
    socket.on('trip:seatSelectRejected', (data) => {
      if (data.tripId === tripId) {
        setSelectedSeats((prev) => prev.filter((s) => s !== data.seatNumber));
        setHeldSeats((prev) => prev.includes(data.seatNumber) ? prev : [...prev, data.seatNumber]);
      } else if (returnTripId && data.tripId === returnTripId) {
        setReturnSelectedSeats((prev) => prev.filter((s) => s !== data.seatNumber));
        setReturnHeldSeats((prev) => prev.includes(data.seatNumber) ? prev : [...prev, data.seatNumber]);
      }
      setError('تم اختيار هذا المقعد من راكب آخر — فضلاً اختر مقعداً آخر');
    });

    return () => socket.disconnect();
  }, [tripId, returnTripId]);

  const maxSeats = (trip) => Math.min(requestedSeats, trip?.availableSeats || requestedSeats);

  // Smart Group Transportation System (Phase 1) — "auto-select seats for my
  // group": attempts an adjacent block server-side; the passenger can still
  // manually adjust afterward via toggleOutboundSeat. When seats must be
  // split across rows (adjacent: false), require explicit confirmation
  // before applying — never silently split without the passenger agreeing.
  const autoSelectSeats = async () => {
    setAutoSuggesting(true);
    try {
      const res = await tripAPI.getGroupSeatSuggestion(tripId, maxSeats(trip));
      const { seats, adjacent } = res.data.data;
      if (!adjacent && !window.confirm('تعذر إيجاد مقاعد متجاورة بالكامل لمجموعتكم — سيتم توزيع المقاعد على أكثر من صف. هل ترغب بالمتابعة؟')) {
        return;
      }
      setSelectedSeats(seats);
    } catch { /* non-critical — manual selection still works */ }
    finally { setAutoSuggesting(false); }
  };

  const toggleOutboundSeat = (n) => {
    if (trip.bookedSeats?.includes(n) || heldSeats.includes(n)) return;
    setSelectedSeats(s => {
      if (s.includes(n)) {
        socketRef.current?.emit('seat:deselect', { tripId, seatNumber: n });
        return s.filter(x => x !== n);
      }
      if (s.length >= maxSeats(trip)) return s;
      // Broadcast the soft hold immediately so every other viewer's grid
      // grays this seat out right away (seatHoldService.js).
      socketRef.current?.emit('seat:select', { tripId, seatNumber: n });
      return [...s, n];
    });
  };

  const toggleReturnSeat = (n) => {
    if (returnTrip.bookedSeats?.includes(n) || returnHeldSeats.includes(n)) return;
    setReturnSelectedSeats(s => {
      if (s.includes(n)) {
        socketRef.current?.emit('seat:deselect', { tripId: returnTripId, seatNumber: n });
        return s.filter(x => x !== n);
      }
      if (s.length >= maxSeats(returnTrip)) return s;
      socketRef.current?.emit('seat:select', { tripId: returnTripId, seatNumber: n });
      return [...s, n];
    });
  };

  // Build passenger detail rows from a seat list; prefill from a prior list if available,
  // falling back to the logged-in user's name/phone for the first seat.
  const buildRows = (seats, prefill = []) =>
    [...seats].sort((a, b) => a - b).map((seat, i) => ({
      seat,
      name:     prefill[i]?.name     || (i === 0 ? storedUser.name  || '' : ''),
      phone:    prefill[i]?.phone    || (i === 0 ? storedUser.phone || '' : ''),
      whatsapp: prefill[i]?.whatsapp || '',
    }));

  const updatePassenger = (i, field, value) =>
    setPassengerDetails(prev => { const u = [...prev]; u[i] = { ...u[i], [field]: value }; return u; });

  const updateReturnPassenger = (i, field, value) =>
    setReturnPassengerDetails(prev => { const u = [...prev]; u[i] = { ...u[i], [field]: value }; return u; });

  // Name must have at least 4 words (full quadruple name per official documents)
  const hasFullName = (name) => name.trim().split(/\s+/).filter(w => w.length > 0).length >= 4;
  const nameWordCount = (name) => name.trim().split(/\s+/).filter(w => w.length > 0).length;

  const allOutboundFilled = passengerDetails.length > 0 &&
    passengerDetails.every(p => p.name.trim() && hasFullName(p.name) && p.phone.trim());
  const allReturnFilled   = !returnTripId || (returnPassengerDetails.length > 0 &&
    returnPassengerDetails.every(p => p.name.trim() && hasFullName(p.name) && p.phone.trim()));
  const allPassengersFilled = allOutboundFilled && allReturnFilled;

  const outboundTotal = selectedSeats.length * parseFloat(trip?.price || 0);
  const returnTotal   = returnSelectedSeats.length * parseFloat(returnTrip?.price || 0);
  const grandTotal    = returnTripId ? outboundTotal + returnTotal : outboundTotal;

  const promoDiscountAmount = promoApplied
    ? Math.round(grandTotal * promoApplied.discountPercent / 100)
    : 0;
  const finalTotal = grandTotal - promoDiscountAmount;
  const discountedOutbound = grandTotal > 0
    ? outboundTotal - Math.round(promoDiscountAmount * outboundTotal / grandTotal)
    : outboundTotal;
  const discountedReturn = grandTotal > 0
    ? returnTotal - Math.round(promoDiscountAmount * returnTotal / grandTotal)
    : returnTotal;

  const isSameCompany   = returnTripId && trip?.companyId === returnTrip?.companyId;
  const hasTwoReceipts  = returnTripId && !isSameCompany;

  const tripHasPromo = (!!trip?.promo && (trip.promo.discountPercent || 0) > 0)
    || (!!returnTrip?.promo && (returnTrip.promo.discountPercent || 0) > 0);

  const goToPassengers = () => {
    // Called after outbound seats (one-way) or return seats (round-trip)
    const outRows = buildRows(selectedSeats);
    setPassengerDetails(outRows);
    if (returnTripId) {
      // Pre-fill return with same names/phones, different seat numbers
      setReturnPassengerDetails(buildRows(returnSelectedSeats, outRows));
    }
    setStep('passengers');
  };

  const proceedToBank = async () => {
    const shortName = [...passengerDetails, ...returnPassengerDetails]
      .find(p => p.name.trim() && !hasFullName(p.name));
    if (shortName) {
      setError(isRTL
        ? `الاسم "${shortName.name}" غير مكتمل — يجب إدخال الاسم الرباعي (4 كلمات) كما في الوثائق الرسمية`
        : `Name "${shortName.name}" is incomplete — enter the full 4-part name as in official documents`);
      return;
    }
    if (!allPassengersFilled) {
      setError(isRTL ? 'يرجى إدخال بيانات جميع المسافرين' : 'Please fill in all passenger details');
      return;
    }
    setError('');
    try {
      const reqs = [tripAPI.getPaymentInfo(tripId)];
      if (returnTripId) reqs.push(tripAPI.getPaymentInfo(returnTripId));
      const [outInfo, retInfo] = await Promise.all(reqs);
      setPaymentInfo({ outbound: outInfo.data.data, returnTrip: retInfo?.data?.data || null });
    } catch {
      // non-fatal — BankAccountsCard handles empty array gracefully
    }
    setStep('bank');
  };

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    setPromoError('');
    try {
      const res = await promoAPI.validate(promoCode);
      setPromoApplied(res.data.data);
      setPromoCode('');
    } catch (err) {
      setPromoError(err.response?.data?.message || 'الكود غير صالح أو منتهي الصلاحية');
      setPromoApplied(null);
    } finally {
      setPromoValidating(false);
    }
  };

  const createBooking = async () => {
    setLoading(true); setError('');
    try {
      const groupFields = bookingType !== 'individual' ? { bookingType, groupName: groupName || undefined } : {};
      const bookingEndpoint = bookingType !== 'individual' ? '/bookings/group' : '/bookings';

      const outRes = await api.post(bookingEndpoint, {
        tripId,
        seats: selectedSeats,
        passengerDetails,
        ...groupFields,
        ...(promoApplied ? { promoCode: promoApplied.code } : {}),
      });
      setBookingId(outRes.data.data.bookingId);
      setBookingNumber(outRes.data.data.bookingNumber);

      if (returnTripId) {
        const retRes = await api.post(bookingEndpoint, {
          tripId: returnTripId,
          seats: returnSelectedSeats,
          passengerDetails: returnPassengerDetails,
          ...groupFields,
        });
        setReturnBookingId(retRes.data.data.bookingId);
        setReturnBookingNumber(retRes.data.data.bookingNumber);
      }

      setStep('receipt');
    } catch (e) {
      setError(e.response?.data?.message || t.bookingFailed || 'Booking failed.');
    } finally { setLoading(false); }
  };

  const uploadReceipt = async () => {
    if (!receipt) { setError(t.selectReceiptError); return; }
    setLoading(true); setError('');
    try {
      const outboundPhone = passengerDetails[0]?.phone || '';
      if (hasTwoReceipts) {
        const fd = new FormData();
        fd.append('receipt', receipt);
        if (outboundPhone) fd.append('passengerPhone', outboundPhone);
        await api.post(`/bookings/${bookingId}/payment-proof`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setReceipt(null);
        setStep('receipt_return');
      } else {
        const ids = returnTripId ? [bookingId, returnBookingId] : [bookingId];
        await Promise.all(ids.map(bid => {
          const fd = new FormData();
          fd.append('receipt', receipt);
          if (outboundPhone) fd.append('passengerPhone', outboundPhone);
          return api.post(`/bookings/${bid}/payment-proof`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }));
        setStep('done');
      }
    } catch (e) {
      setError(e.response?.data?.message || t.uploadFailed || 'Upload failed.');
    } finally { setLoading(false); }
  };

  const uploadReturnReceipt = async () => {
    if (!receipt) { setError(t.selectReceiptError); return; }
    setLoading(true); setError('');
    try {
      const returnPhone = returnPassengerDetails[0]?.phone || passengerDetails[0]?.phone || '';
      const fd = new FormData();
      fd.append('receipt', receipt);
      if (returnPhone) fd.append('passengerPhone', returnPhone);
      await api.post(`/bookings/${returnBookingId}/payment-proof`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStep('done');
    } catch (e) {
      setError(e.response?.data?.message || t.uploadFailed || 'Upload failed.');
    } finally { setLoading(false); }
  };

  const formatTime = (dt) => new Date(dt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });

  if (!trip || (returnTripId && !returnTrip)) return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="flex items-center justify-center h-screen text-blue-600">
      {t.loading}
    </div>
  );

  const seatLegend = (
    <div className="flex gap-4 mb-4 text-xs">
      <span className="flex items-center gap-1.5">
        <span className="w-5 h-5 bg-white border border-gray-300 rounded inline-block" />{t.available}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-5 h-5 bg-blue-700 rounded inline-block" />{t.selected}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-5 h-5 bg-red-50 border border-red-200 rounded inline-block" />{t.booked}
      </span>
    </div>
  );

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-[#F5F9FF]">
      <AppNavbar />

      <BookingContextBar
        trip={trip}
        returnTrip={returnTrip}
        step={step}
        selectedSeats={selectedSeats}
        returnSelectedSeats={returnSelectedSeats}
        grandTotal={finalTotal}
        bookingNumber={bookingNumber}
      />

      <div className="max-w-2xl mx-auto px-4 md:px-6 pt-5 pb-16 space-y-4">

        {/* Back + progress row */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#E5E7EB] text-gray-500 hover:border-[#C7D2FE] hover:text-[#1E40AF] hover:bg-[#EEF2FF] transition-all text-sm font-semibold flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d={isRTL ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
            </svg>
            {isRTL ? 'رجوع' : 'Back'}
          </button>
          <div className="flex-1 bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_1px_4px_rgba(17,24,39,.04)] px-4 py-3.5">
            <BookingProgressBar step={step} isRTL={isRTL} />
          </div>
        </div>

        {/* Round-trip sub-step detail bar */}
        {returnTripId && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_1px_4px_rgba(17,24,39,.04)] px-4 py-2">
            <RoundTripProgress step={step} isRTL={isRTL} hasTwoReceipts={hasTwoReceipts} />
          </div>
        )}

        {/* Trip summary card */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_12px_rgba(7,30,82,.06)] p-4">
          {returnTripId ? (
            <div className="space-y-2">
              <div className={`flex justify-between items-start pb-2 border-b transition-opacity
                ${step === 'seats' ? 'opacity-100' : 'opacity-50'}`}>
                <div>
                  <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wide">{t.outboundTrip}</span>
                  <p className="font-bold text-gray-800 text-sm">{trip.fromCity} → {trip.toCity}</p>
                  <p className="text-gray-600 font-semibold text-xs">{toArNum(formatTime(trip.departureTime))} · {trip.company?.name}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600 text-sm">{arPrice(trip.price)} ج.س</p>
                  {selectedSeats.length > 0 && (
                    <p className="text-xs text-gray-600 font-semibold">{toArNum(selectedSeats.length)} مقعد</p>
                  )}
                </div>
              </div>
              <div className={`flex justify-between items-start transition-opacity
                ${step === 'return_seats' ? 'opacity-100' : 'opacity-50'}`}>
                <div>
                  <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wide">{t.returnTripLabel}</span>
                  <p className="font-bold text-gray-800 text-sm">{returnTrip.fromCity} → {returnTrip.toCity}</p>
                  <p className="text-gray-600 font-semibold text-xs">{toArNum(formatTime(returnTrip.departureTime))} · {returnTrip.company?.name}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600 text-sm">{arPrice(returnTrip.price)} ج.س</p>
                  {returnSelectedSeats.length > 0 && (
                    <p className="text-xs text-gray-600 font-semibold">{toArNum(returnSelectedSeats.length)} مقعد</p>
                  )}
                </div>
              </div>
              {['bank', 'receipt', 'done'].includes(step) && (
                <div className="flex justify-between items-center border-t pt-2">
                  <span className="text-sm font-bold text-gray-800">{t.roundTripTotal}</span>
                  <span className="text-lg font-bold text-green-600">{arPrice(grandTotal)} ج.س</span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <div>
                <p className="font-bold text-gray-800 text-lg">{trip.fromCity} {isRTL ? '←' : '→'} {trip.toCity}</p>
                <p className="text-gray-700 font-semibold text-sm">{toArNum(formatTime(trip.departureTime))} ← {toArNum(formatTime(trip.arrivalTime))}</p>
                <p className="text-blue-600 text-sm font-medium">
                  {trip.company?.name}
                  {trip.bus?.vehicleType && <span className="text-gray-500"> · {trip.bus.vehicleType.icon} {trip.bus.vehicleType.name}</span>}
                </p>
              </div>
              <p className="text-2xl font-bold text-green-600">{arPrice(trip.price)} ج.س</p>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3.5 text-sm font-medium flex items-center gap-2.5">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" d="M12 8v4m0 4h.01"/>
            </svg>
            {error}
          </div>
        )}

        {/* ─── Step: Outbound Seats ──────────────────────────────────────────── */}
        {step === 'seats' && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_12px_rgba(7,30,82,.06)] p-5">
            <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
              <h2 className="font-semibold text-gray-700">
                {returnTripId
                  ? (isRTL ? '🛫 اختر مقاعد الذهاب' : '🛫 Select Outbound Seats')
                  : t.selectSeats}
              </h2>
              <span className={`text-xs font-bold px-3 py-1 rounded-full border
                ${selectedSeats.length === maxSeats(trip)
                  ? 'bg-green-100 text-green-700 border-green-200'
                  : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                {toArNum(selectedSeats.length)} / {toArNum(maxSeats(trip))} مقعد
              </span>
            </div>

            {trip.availableSeats < requestedSeats && (
              <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 flex items-center gap-2">
                <span>⚠️</span>
                <span>
                  {`متاح ${toArNum(trip.availableSeats)} مقعد فقط في هذه الرحلة بدلاً من ${toArNum(requestedSeats)}`}
                </span>
              </div>
            )}

            {returnTripId && (
              <p className="text-xs text-gray-600 font-semibold mb-3">
                ستختار مقاعد رحلة الإياب في الخطوة التالية
              </p>
            )}

            {bookingType !== 'individual' && (
              <button
                type="button"
                onClick={autoSelectSeats}
                disabled={autoSuggesting}
                className="w-full mb-3 bg-blue-50 hover:bg-blue-100 disabled:opacity-60 text-[#1E3A8A] border border-blue-100 text-xs font-bold px-4 py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {autoSuggesting ? '...جارٍ الاقتراح' : '✨ اختيار المقاعد تلقائياً لمجموعتنا'}
              </button>
            )}

            <div className="flex justify-center">
              <SeatDiagram
                bookedSeats={[...(trip.bookedSeats || []), ...(trip.blockedSeats || []), ...heldSeats]}
                selectedSeats={selectedSeats}
                onToggle={toggleOutboundSeat}
                t={t}
                bus={trip.bus}
                totalSeats={(trip.bookedSeats?.length || 0) + trip.availableSeats}
              />
            </div>
            {selectedSeats.length > 0 && (
              <div className="mt-5 bg-[#EEF2FF] border border-[#C7D2FE] rounded-2xl px-4 py-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] text-gray-500 font-medium">{t.seatsLabel}</p>
                    <p className="font-black text-[#1E3A8A] text-base">{[...selectedSeats].sort((a, b) => a - b).map(toArNum).join('، ')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[13px] text-gray-600 font-semibold">{t.total}</p>
                    <p className="font-black text-emerald-600 text-xl tabular-nums">{arPrice(outboundTotal)} ج.س</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPassengerDetails(buildRows(selectedSeats));
                    setStep(returnTripId ? 'return_seats' : 'passengers');
                  }}
                  className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] active:scale-[.98] text-white py-3.5 rounded-xl text-[15px] font-black transition-all shadow-[0_4px_14px_rgba(30,64,175,.35)] flex items-center justify-center gap-2"
                >
                  {returnTripId
                    ? (isRTL ? 'التالي: اختر مقاعد الإياب' : 'Next: Select Return Seats')
                    : (isRTL ? 'التالي: بيانات المسافرين' : 'Next: Passenger Details')}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        {/* ─── Step: Return Seats ───────────────────────────────────────────── */}
        {step === 'return_seats' && returnTripId && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_12px_rgba(7,30,82,.06)] p-5">
            <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
              <h2 className="font-semibold text-gray-700">
                {isRTL ? '🛬 اختر مقاعد الإياب' : '🛬 Select Return Seats'}
              </h2>
              <span className={`text-xs font-bold px-3 py-1 rounded-full border
                ${returnSelectedSeats.length === maxSeats(returnTrip)
                  ? 'bg-green-100 text-green-700 border-green-200'
                  : 'bg-purple-50 text-purple-600 border-purple-100'}`}>
                {toArNum(returnSelectedSeats.length)} / {toArNum(maxSeats(returnTrip))} مقعد
              </span>
            </div>

            {returnTrip.availableSeats < requestedSeats && (
              <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 flex items-center gap-2">
                <span>⚠️</span>
                <span>
                  {`متاح ${toArNum(returnTrip.availableSeats)} مقعد فقط في رحلة الإياب بدلاً من ${toArNum(requestedSeats)}`}
                </span>
              </div>
            )}

            <div className="flex justify-center">
              <SeatDiagram
                bookedSeats={[...(returnTrip.bookedSeats || []), ...(returnTrip.blockedSeats || []), ...returnHeldSeats]}
                selectedSeats={returnSelectedSeats}
                onToggle={toggleReturnSeat}
                t={t}
                bus={returnTrip.bus}
                totalSeats={(returnTrip.bookedSeats?.length || 0) + returnTrip.availableSeats}
              />
            </div>
            {returnSelectedSeats.length > 0 && (
              <div className="mt-5 bg-purple-50 border border-purple-200 rounded-2xl px-4 py-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] text-purple-500 font-medium">{t.seatsLabel}</p>
                    <p className="font-black text-purple-800 text-base">{[...returnSelectedSeats].sort((a, b) => a - b).map(toArNum).join('، ')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[13px] text-purple-600 font-semibold">{t.total}</p>
                    <p className="font-black text-emerald-600 text-xl tabular-nums">{arPrice(returnTotal)} ج.س</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setReturnPassengerDetails(buildRows(returnSelectedSeats, passengerDetails));
                    setStep('passengers');
                  }}
                  className="w-full bg-purple-700 hover:bg-purple-800 active:scale-[.98] text-white py-3.5 rounded-xl text-[15px] font-black transition-all shadow-[0_4px_14px_rgba(126,34,206,.3)] flex items-center justify-center gap-2"
                >
                  {isRTL ? 'التالي: بيانات المسافرين' : 'Next: Passenger Details'}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                  </svg>
                </button>
              </div>
            )}
            <button onClick={() => setStep('seats')}
              className="mt-3 text-sm text-gray-600 font-semibold hover:text-gray-800 block">
              {t.back}
            </button>
          </div>
        )}

        {/* ─── Step: Passenger Details ──────────────────────────────────────── */}
        {step === 'passengers' && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_12px_rgba(7,30,82,.06)] p-5">
            <h2 className="font-bold text-gray-800 text-center mb-1 text-lg">
              {isRTL ? '👤 بيانات المسافرين' : '👤 Passenger Details'}
            </h2>
            <p className="text-center text-xs text-gray-600 font-semibold mb-4">
              {isRTL
                ? 'يرجى إدخال الاسم ورقم الهاتف لكل مسافر'
                : 'Enter name and phone number for each passenger'}
            </p>

            {/* ── Compact 4-part name reminder ──────────────────────────── */}
            <div className="mb-4 flex items-center gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="text-amber-500 flex-shrink-0">
                <circle cx="12" cy="12" r="10"/><path strokeLinecap="round" d="M12 8v4m0 4h.01"/>
              </svg>
              <p className="text-xs text-amber-800 leading-relaxed">
                {isRTL
                  ? <><strong>الاسم الرباعي إلزامي (4 كلمات)</strong> كما في الوثائق الرسمية · مثال: محمد أحمد علي محمد</>
                  : <><strong>Full 4-part name required</strong> as in official documents · e.g.: Mohammed Ahmed Ali Mohammed</>
                }
              </p>
            </div>

            {/* Outbound passengers */}
            {returnTripId && (
              <p className="text-xs font-bold text-blue-600 mb-2 flex items-center gap-1">
                🛫 {isRTL ? 'مسافرو رحلة الذهاب' : 'Outbound Passengers'}
              </p>
            )}
            <div className="space-y-4 mb-5">
              {passengerDetails.map((pd, i) => {
                const wc = nameWordCount(pd.name);
                const nameOk = wc >= 4;
                const nameTouched = pd.name.trim().length > 0;
                return (
                  <div key={i} className="bg-[#F5F9FF] border border-[#E5E7EB] rounded-xl p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm flex-shrink-0 flex-col">
                        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current opacity-60 mb-0.5">
                          <path d="M18 4a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v1H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h1v1a1 1 0 0 0 2 0v-1h8v1a1 1 0 0 0 2 0v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V4z"/>
                        </svg>
                        <span className="text-[11px] leading-none">{toArNum(pd.seat)}</span>
                      </div>
                      <span className="text-xs font-semibold text-gray-700">
                        {`المسافر ${toArNum(i + 1)}`}
                      </span>
                      {nameTouched && (
                        <span className={`ms-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${nameOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {nameOk
                            ? '✓ اسم رباعي'
                            : `✗ ${toArNum(wc)}/٤ كلمات`}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <input
                          type="text"
                          value={pd.name}
                          onChange={(e) => updatePassenger(i, 'name', e.target.value)}
                          placeholder={isRTL ? 'الاسم الرباعي كاملاً' : 'Full 4-part name'}
                          className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white transition-colors
                            ${nameTouched && !nameOk
                              ? 'border-red-300 focus:ring-red-300'
                              : nameTouched && nameOk
                              ? 'border-green-300 focus:ring-green-300'
                              : 'border-gray-200 focus:ring-blue-400'}`}
                        />
                        {nameTouched && !nameOk && (
                          <p className="text-[11px] text-red-500 mt-1">
                            {`أدخل ${toArNum(4 - wc)} كلمة إضافية للاسم الرباعي`}
                          </p>
                        )}
                      </div>
                      <input
                        type="tel"
                        value={pd.phone}
                        onChange={(e) => updatePassenger(i, 'phone', e.target.value)}
                        placeholder={isRTL ? 'رقم الهاتف' : 'Phone number'}
                        className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                        dir="ltr"
                      />
                    </div>
                    {/* WhatsApp number — ticket & booking confirmation sent here */}
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <span className="text-green-600 text-base shrink-0">📱</span>
                      <input
                        type="tel"
                        value={pd.whatsapp || ''}
                        onChange={(e) => updatePassenger(i, 'whatsapp', e.target.value)}
                        placeholder={isRTL ? 'رقم الواتساب (اختياري — يُستخدم لإرسال التذكرة)' : 'WhatsApp number (optional — ticket sent here)'}
                        className="flex-1 bg-transparent text-sm focus:outline-none text-gray-700 placeholder-gray-400"
                        dir="ltr"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Return passengers (round trip only) */}
            {returnTripId && (
              <>
                <p className="text-xs font-bold text-purple-600 mb-2 flex items-center gap-1">
                  🛬 {isRTL ? 'مسافرو رحلة الإياب' : 'Return Passengers'}
                </p>
                <p className="text-xs text-gray-600 font-semibold mb-3">
                  تم ملء البيانات تلقائياً من رحلة الذهاب — يمكنك تعديلها
                </p>
                <div className="space-y-4 mb-5">
                  {returnPassengerDetails.map((pd, i) => {
                    const wc = nameWordCount(pd.name);
                    const nameOk = wc >= 4;
                    const nameTouched = pd.name.trim().length > 0;
                    return (
                      <div key={i} className="bg-purple-50 rounded-xl p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-sm flex-shrink-0 flex-col">
                            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current opacity-60 mb-0.5">
                              <path d="M18 4a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v1H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h1v1a1 1 0 0 0 2 0v-1h8v1a1 1 0 0 0 2 0v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V4z"/>
                            </svg>
                            <span className="text-[11px] leading-none">{toArNum(pd.seat)}</span>
                          </div>
                          <span className="text-xs font-semibold text-gray-700">
                            {`المسافر ${toArNum(i + 1)}`}
                          </span>
                          {nameTouched && (
                            <span className={`ms-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${nameOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                              {nameOk
                                ? '✓ اسم رباعي'
                                : `✗ ${toArNum(wc)}/٤ كلمات`}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <input
                              type="text"
                              value={pd.name}
                              onChange={(e) => updateReturnPassenger(i, 'name', e.target.value)}
                              placeholder={isRTL ? 'الاسم الرباعي كاملاً' : 'Full 4-part name'}
                              className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white transition-colors
                                ${nameTouched && !nameOk
                                  ? 'border-red-300 focus:ring-red-300'
                                  : nameTouched && nameOk
                                  ? 'border-green-300 focus:ring-green-300'
                                  : 'border-purple-200 focus:ring-purple-400'}`}
                            />
                            {nameTouched && !nameOk && (
                              <p className="text-[11px] text-red-500 mt-1">
                                {`أدخل ${toArNum(4 - wc)} كلمة إضافية للاسم الرباعي`}
                              </p>
                            )}
                          </div>
                          <input
                            type="tel"
                            value={pd.phone}
                            onChange={(e) => updateReturnPassenger(i, 'phone', e.target.value)}
                            placeholder={isRTL ? 'رقم الهاتف' : 'Phone number'}
                            className="border border-purple-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white"
                            dir="ltr"
                          />
                        </div>
                        {/* WhatsApp number for return trip */}
                        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                          <span className="text-green-600 text-base shrink-0">📱</span>
                          <input
                            type="tel"
                            value={pd.whatsapp || ''}
                            onChange={(e) => updateReturnPassenger(i, 'whatsapp', e.target.value)}
                            placeholder={isRTL ? 'رقم الواتساب (اختياري — يُستخدم لإرسال التذكرة)' : 'WhatsApp number (optional — ticket sent here)'}
                            className="flex-1 bg-transparent text-sm focus:outline-none text-gray-700 placeholder-gray-400"
                            dir="ltr"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Privacy notice */}
            <p className="text-xs text-gray-600 text-center mt-1 leading-relaxed px-2">
              🔒 يتم استخدام بياناتك فقط لإتمام الحجز وإرسال التذاكر والإشعارات المتعلقة بالرحلة.
            </p>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep(returnTripId ? 'return_seats' : 'seats')}
                className="flex-shrink-0 bg-gray-100 hover:bg-gray-200 text-gray-600 px-5 py-3.5 rounded-xl text-sm font-semibold"
              >
                {t.back}
              </button>
              <button
                onClick={proceedToBank}
                disabled={!allPassengersFilled}
                className="flex-1 bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:bg-gray-200 disabled:cursor-not-allowed text-white py-3.5 rounded-xl text-[15px] font-black transition-all shadow-[0_4px_14px_rgba(30,64,175,.3)] disabled:shadow-none flex items-center justify-center gap-2"
              >
                {isRTL ? 'التالي: تأكيد الحجز' : 'Next: Confirm Booking'}
                {allPassengersFilled && (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                  </svg>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ─── Step: Confirm booking (payment summary) ──────────────────────── */}
        {step === 'bank' && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_12px_rgba(7,30,82,.06)] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-bl from-[#1E3A8A] to-[#1E40AF] px-5 py-4">
              <p className="text-blue-100 text-xs font-semibold uppercase tracking-widest mb-1">{isRTL ? 'ملخص الحجز' : 'Booking Summary'}</p>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-blue-100 text-sm">{isRTL ? 'الإجمالي المطلوب' : 'Amount due'}</p>
                  {promoApplied && (
                    <p className="text-blue-300 text-sm line-through tabular-nums">{arPrice(grandTotal)} ج.س</p>
                  )}
                  <p className="text-white text-3xl font-black tabular-nums">{arPrice(finalTotal)} <span className="text-xl font-bold text-blue-100">ج.س</span></p>
                </div>
                {returnTripId && (
                  <div className="text-right text-blue-100 text-xs space-y-0.5">
                    <p>ذهاب: {arPrice(discountedOutbound)} ج.س</p>
                    <p>إياب: {arPrice(discountedReturn)} ج.س</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Amount line breakdown */}
              {returnTripId ? (
                <div className="bg-[#F5F9FF] border border-[#E5E7EB] rounded-xl p-3 space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>🛫 {t.outboundTrip}</span>
                    <span className="font-mono">{toArNum(selectedSeats.length)} × {arPrice(trip.price)} = <strong className="text-gray-800">{arPrice(outboundTotal)}</strong></span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>🛬 {t.returnTripLabel}</span>
                    <span className="font-mono">{toArNum(returnSelectedSeats.length)} × {arPrice(returnTrip.price)} = <strong className="text-gray-800">{arPrice(returnTotal)}</strong></span>
                  </div>
                </div>
              ) : (
                <div className="bg-[#F5F9FF] border border-[#E5E7EB] rounded-xl px-4 py-3 flex justify-between items-center text-sm text-gray-600">
                  <span>{toArNum(selectedSeats.length)} مقعد × {arPrice(trip.price)} ج.س</span>
                  <span className="font-black text-gray-800 text-base tabular-nums">{arPrice(grandTotal)} ج.س</span>
                </div>
              )}

              {/* Promo code — only shown when the trip's company has an active discount promo */}
              {tripHasPromo && (
              <div className="bg-[#F5F9FF] border border-[#E5E7EB] rounded-xl p-3">
                <p className="text-xs font-bold text-gray-600 mb-2">🏷️ {isRTL ? 'كود الخصم' : 'Promo Code'}</p>
                {promoApplied ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
                    <div>
                      <p className="text-green-700 font-black text-sm" dir="ltr">{promoApplied.code}</p>
                      <p className="text-green-600 text-xs">{promoApplied.title} · خصم {promoApplied.discountPercent}% (يُخصم {arPrice(promoDiscountAmount)} ج.س)</p>
                    </div>
                    <button onClick={() => setPromoApplied(null)}
                      className="text-xs text-red-500 hover:text-red-700 font-semibold transition">
                      {isRTL ? 'إلغاء' : 'Remove'}
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                      onKeyDown={(e) => e.key === 'Enter' && applyPromo()}
                      placeholder={isRTL ? 'أدخل الكود...' : 'Enter code...'}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-400"
                      dir="ltr"
                    />
                    <button
                      onClick={applyPromo}
                      disabled={!promoCode.trim() || promoValidating}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-bold transition"
                    >
                      {promoValidating ? '...' : (isRTL ? 'تطبيق' : 'Apply')}
                    </button>
                  </div>
                )}
                {promoError && <p className="text-xs text-red-500 mt-1.5">{promoError}</p>}
              </div>
              )}

              {/* Discount line */}
              {promoApplied && (
                <div className="flex justify-between items-center bg-green-50 border border-green-200 rounded-xl px-4 py-2.5 text-sm">
                  <span className="text-green-700 font-semibold">🏷️ خصم {promoApplied.discountPercent}% ({promoApplied.code})</span>
                  <span className="text-green-700 font-black">− {arPrice(promoDiscountAmount)} ج.س</span>
                </div>
              )}

              {/* Passenger summary */}
              <div>
                {returnTripId && (
                  <p className="text-xs font-black text-blue-600 mb-2 flex items-center gap-1">🛫 {isRTL ? 'مسافرو الذهاب' : 'Outbound'}</p>
                )}
                <div className="space-y-1.5">
                  {passengerDetails.map((pd, i) => (
                    <div key={i} className="flex items-center gap-3 bg-blue-50 rounded-xl px-3 py-2.5 text-sm">
                      <span className="bg-[#1E40AF] text-white font-black text-[11px] px-2 py-0.5 rounded-lg flex-shrink-0 tabular-nums">{toArNum(pd.seat)}</span>
                      <span className="font-semibold text-gray-800 flex-1 truncate">{pd.name}</span>
                      <span className="text-gray-600 text-xs font-mono flex-shrink-0" dir="ltr">{pd.phone}</span>
                    </div>
                  ))}
                </div>
                {returnTripId && returnPassengerDetails.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-black text-purple-600 mb-2 flex items-center gap-1">🛬 {isRTL ? 'مسافرو الإياب' : 'Return'}</p>
                    <div className="space-y-1.5">
                      {returnPassengerDetails.map((pd, i) => (
                        <div key={i} className="flex items-center gap-3 bg-purple-50 rounded-xl px-3 py-2.5 text-sm">
                          <span className="bg-purple-600 text-white font-black text-[11px] px-2 py-0.5 rounded-lg flex-shrink-0 tabular-nums">{toArNum(pd.seat)}</span>
                          <span className="font-semibold text-gray-800 flex-1 truncate">{pd.name}</span>
                          <span className="text-gray-600 text-xs font-mono flex-shrink-0" dir="ltr">{pd.phone}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {hasTwoReceipts && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5">⚠️</span>
                  <span>{isRTL ? 'هذه رحلتان من شركتين مختلفتين — ستحتاج لرفع إيصال منفصل لكل شركة.' : 'Two different companies — you will upload a separate receipt for each.'}</span>
                </div>
              )}

              {!isPassengerLoggedIn && (
                <LegalAcceptanceCheckbox checked={legalAccepted} onChange={setLegalAccepted} />
              )}

              <div className="flex gap-3 pt-1">
                <button onClick={() => setStep('passengers')}
                  className="flex-shrink-0 bg-gray-100 hover:bg-gray-200 text-gray-600 px-5 py-3.5 rounded-xl text-sm font-semibold">
                  {t.back}
                </button>
                <button onClick={createBooking} disabled={loading || (!isPassengerLoggedIn && !legalAccepted)}
                  className="flex-1 bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:bg-gray-200 disabled:cursor-not-allowed text-white py-3.5 rounded-xl text-[15px] font-black transition-all shadow-[0_4px_14px_rgba(30,64,175,.3)] disabled:shadow-none">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                      </svg>
                      {t.creatingBooking}
                    </span>
                  ) : t.confirmBooking}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Step: Receipt (outbound, or single for one-way / same-company) ── */}
        {step === 'receipt' && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_12px_rgba(7,30,82,.06)] overflow-hidden">
            {/* Booking number hero */}
            <div className="bg-[#EEF2FF] border-b border-[#C7D2FE] px-5 py-4">
              <p className="text-[#1E40AF] text-xs font-bold uppercase tracking-widest mb-1">
                {hasTwoReceipts ? '🛫 الخطوة ١ من ٢' : 'تم إنشاء الحجز'}
              </p>
              {hasTwoReceipts ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 font-semibold text-xs">رقم حجز الذهاب</p>
                    <p className="font-black text-[#1E3A8A] text-2xl font-mono tracking-wider">#{bookingNumber}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-600 font-semibold text-xs">المبلغ المطلوب</p>
                    <p className="font-black text-emerald-600 text-xl tabular-nums">{arPrice(discountedOutbound)} ج.س</p>
                  </div>
                </div>
              ) : returnTripId ? (
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-500 text-xs">ذهاب</span>
                    <span className="font-black text-[#1E3A8A] font-mono">#{bookingNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-purple-500 text-xs">إياب</span>
                    <span className="font-black text-purple-700 font-mono">#{returnBookingNumber}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-[#C7D2FE] pt-2 mt-2">
                    <span className="text-gray-500 text-xs">{isRTL ? 'الإجمالي' : 'Total'}</span>
                    <span className="font-black text-emerald-600 text-xl tabular-nums">{arPrice(finalTotal)} ج.س</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 font-semibold text-xs">رقم الحجز</p>
                    <p className="font-black text-[#1E3A8A] text-2xl font-mono tracking-wider">#{bookingNumber}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-600 font-semibold text-xs">المبلغ المطلوب</p>
                    <p className="font-black text-emerald-600 text-xl tabular-nums">{arPrice(finalTotal)} ج.س</p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 space-y-4">
              <BankAccountsCard
                company={{ ...trip?.company, bankAccounts: paymentInfo?.outbound?.bankAccounts ?? trip?.company?.bankAccounts }}
                amount={hasTwoReceipts ? discountedOutbound : finalTotal}
                accentColor="blue"
                isRTL={isRTL}
              />

              <div>
                <p className="text-sm font-bold text-gray-700 mb-2">
                  {hasTwoReceipts ? (isRTL ? '📎 ارفع إيصال تحويل الذهاب' : '📎 Upload Outbound Receipt') : t.uploadReceiptTitle}
                </p>
                <label className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all
                  ${receipt ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-[#F5F9FF] hover:border-[#1E40AF] hover:bg-[#EEF2FF]'}`}>
                  <input type="file" accept="image/*,application/pdf" className="hidden"
                    onChange={(e) => setReceipt(e.target.files[0])} />
                  {receipt ? (
                    <>
                      <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-2 text-2xl">✅</div>
                      <p className="text-emerald-700 font-bold text-sm">{receipt.name}</p>
                      <p className="text-emerald-500 text-xs mt-1">{isRTL ? 'انقر لتغيير الملف' : 'Tap to change file'}</p>
                    </>
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-2 text-2xl">📎</div>
                      <p className="text-gray-700 font-bold text-sm">{t.clickToUpload}</p>
                      <p className="text-gray-600 font-semibold text-xs mt-1">صورة أو PDF</p>
                    </>
                  )}
                </label>
              </div>

              <button onClick={uploadReceipt} disabled={!receipt || loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white py-3.5 rounded-xl text-[15px] font-black transition-all shadow-[0_4px_14px_rgba(5,150,105,.3)] disabled:shadow-none flex items-center justify-center gap-2">
                {loading ? (
                  <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg>{t.uploading}</>
                ) : hasTwoReceipts ? (isRTL ? 'إرسال إيصال الذهاب' : 'Submit Outbound Receipt')
                  : t.submitReceipt}
              </button>
            </div>
          </div>
        )}

        {/* ─── Step: Return Receipt (different-company only) ────────────────── */}
        {step === 'receipt_return' && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_12px_rgba(7,30,82,.06)] overflow-hidden">
            {/* Success indicator for outbound */}
            <div className="bg-emerald-50 border-b border-emerald-200 px-5 py-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
              </svg>
              <p className="text-emerald-700 text-sm font-bold">{isRTL ? 'تم رفع إيصال الذهاب بنجاح' : 'Outbound receipt submitted'}</p>
            </div>

            {/* Booking number hero */}
            <div className="bg-purple-50 border-b border-purple-200 px-5 py-4">
              <p className="text-purple-500 text-xs font-bold uppercase tracking-widest mb-1">
                🛬 الخطوة ٢ من ٢ — إيصال الإياب
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 font-semibold text-xs">رقم حجز الإياب</p>
                  <p className="font-black text-purple-800 text-2xl font-mono tracking-wider">#{returnBookingNumber}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-600 font-semibold text-xs">المبلغ المطلوب</p>
                  <p className="font-black text-emerald-600 text-xl tabular-nums">{arPrice(discountedReturn)} ج.س</p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <BankAccountsCard
                company={{ ...returnTrip?.company, bankAccounts: paymentInfo?.returnTrip?.bankAccounts ?? returnTrip?.company?.bankAccounts }}
                amount={discountedReturn}
                accentColor="purple"
                isRTL={isRTL}
              />

              <div>
                <p className="text-sm font-bold text-gray-700 mb-2">📎 {isRTL ? 'ارفع إيصال تحويل الإياب' : 'Upload Return Transfer Receipt'}</p>
                <label className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all
                  ${receipt ? 'border-emerald-400 bg-emerald-50' : 'border-purple-200 bg-purple-50 hover:border-purple-500 hover:bg-purple-100'}`}>
                  <input type="file" accept="image/*,application/pdf" className="hidden"
                    onChange={(e) => setReceipt(e.target.files[0])} />
                  {receipt ? (
                    <>
                      <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-2 text-2xl">✅</div>
                      <p className="text-emerald-700 font-bold text-sm">{receipt.name}</p>
                      <p className="text-emerald-500 text-xs mt-1">{isRTL ? 'انقر لتغيير الملف' : 'Tap to change file'}</p>
                    </>
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-2 text-2xl">📎</div>
                      <p className="text-gray-700 font-bold text-sm">{t.clickToUpload}</p>
                      <p className="text-gray-600 font-semibold text-xs mt-1">صورة أو PDF</p>
                    </>
                  )}
                </label>
              </div>

              <button onClick={uploadReturnReceipt} disabled={!receipt || loading}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white py-3.5 rounded-xl text-[15px] font-black transition-all shadow-[0_4px_14px_rgba(126,34,206,.3)] disabled:shadow-none flex items-center justify-center gap-2">
                {loading ? (
                  <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg>{t.uploading}</>
                ) : (isRTL ? 'إرسال إيصال الإياب وإتمام الحجز' : 'Submit Return Receipt & Finish')}
              </button>
            </div>
          </div>
        )}

        {/* ─── Step: Done ───────────────────────────────────────────────────── */}
        {step === 'done' && (
          <>
            <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_12px_rgba(7,30,82,.06)] overflow-hidden">
              {/* Success hero */}
              <div className="bg-gradient-to-b from-emerald-500 to-emerald-600 px-5 pt-8 pb-6 text-center">
                <div className="w-20 h-20 rounded-full bg-white/20 border-4 border-white/40 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-black text-white mb-1">
                  {isRTL ? '🎉 تم الحجز بنجاح!' : '🎉 Booking Confirmed!'}
                </h2>
                <p className="text-emerald-100 text-sm">{t.awaitingMsg}</p>
              </div>

              <div className="p-5 space-y-3">
                {/* Booking number(s) with copy */}
                {returnTripId ? (
                  <div className="space-y-2">
                    {[
                      { label: 'رقم حجز الذهاب', num: bookingNumber, color: 'blue' },
                      { label: 'رقم حجز الإياب', num: returnBookingNumber, color: 'purple' },
                    ].map(({ label, num, color }) => (
                      <div key={num} className={`flex items-center justify-between rounded-xl px-4 py-3 border
                        ${color === 'blue' ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'}`}>
                        <span className={`text-xs font-bold ${color === 'blue' ? 'text-blue-600' : 'text-purple-600'}`}>{label}</span>
                        <div className="flex items-center gap-2">
                          <span dir="ltr" className={`font-black font-mono text-lg ${color === 'blue' ? 'text-blue-800' : 'text-purple-800'}`}>#{num}</span>
                          <button
                            onClick={() => navigator.clipboard?.writeText(num)}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                            title={isRTL ? 'نسخ' : 'Copy'}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl px-4 py-3.5">
                    <div>
                      <p className="text-gray-600 font-semibold text-xs">{t.bookingNumberLabel}</p>
                      <p dir="ltr" className="font-black font-mono text-2xl text-[#1E3A8A] tracking-wider">#{bookingNumber}</p>
                    </div>
                    <button
                      onClick={() => navigator.clipboard?.writeText(bookingNumber)}
                      className="flex items-center gap-1.5 text-[#1E40AF] hover:text-[#1D4ED8] text-xs font-bold border border-[#C7D2FE] px-3 py-1.5 rounded-lg hover:bg-white transition-all"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                      {isRTL ? 'نسخ' : 'Copy'}
                    </button>
                  </div>
                )}

                {/* Action buttons */}
                <button onClick={() => navigate('/bookings')}
                  className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] text-white py-3.5 rounded-xl text-[15px] font-black transition-all shadow-[0_4px_14px_rgba(30,64,175,.3)]">
                  {t.viewMyBookings}
                </button>
                <button onClick={() => navigate('/')}
                  className="w-full border border-[#E5E7EB] text-gray-500 py-3 rounded-xl text-sm font-semibold hover:border-[#C7D2FE] hover:text-[#1E40AF] hover:bg-[#EEF2FF] transition-all">
                  {t.searchMoreTrips}
                </button>
              </div>
            </div>

            {/* Post-booking account creation CTA — shown to guests only */}
            {!isPassengerLoggedIn && (
              <div className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] rounded-2xl p-5 text-white">
                <p className="font-black text-lg mb-1">{isRTL ? 'احفظ حجزك — أنشئ حساباً' : 'Save Your Booking'}</p>
                <p className="text-blue-200 text-sm mb-4">
                  {isRTL ? 'أنشئ حسابك لمتابعة حجوزاتك وتسريع الحجز في المرة القادمة' : 'Create an account to track bookings and speed up future reservations'}
                </p>
                <ul className="space-y-2 mb-5" dir="rtl">
                  {['تتبع حالة الحجز فورياً', 'استلام التذكرة على واتساب', 'إشعارات الخاصة بالرحلة', 'تسريع الحجز القادم', 'الاطلاع على عروض الخصم على الرحلات'].map(b => (
                    <li key={b} className="flex items-center gap-2 text-blue-100 text-sm">
                      <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                      </svg>
                      {b}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate('/login?mode=register')}
                  className="w-full bg-white text-[#1E3A8A] py-3 rounded-xl text-sm font-black hover:bg-blue-50 transition">
                  {isRTL ? 'إنشاء حساب مجاني' : 'Create Free Account'}
                </button>
                <button onClick={() => navigate('/')}
                  className="w-full mt-2 text-blue-300 hover:text-white text-xs py-1.5 transition">
                  {isRTL ? 'لا شكراً، متابعة كزائر' : 'No thanks, continue as guest'}
                </button>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default PassengerBookPage;
